import { useState } from 'react';
import type { LineAmountType } from '@domain';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';

/**
 * What CFOAI has given this client.
 *
 * Two switches, deliberately separate, because they answer two different
 * questions and the answers do not have to match:
 *
 *   Training      a section in this product. Works on its own.
 *   Xero Projects a permission on the client's Xero connection, which adds
 *                 trainer time tracking inside Xero. Not needed for
 *                 Training to work.
 *
 * Only the practice sees this card. A client turning on their own Training
 * section would be harmless; a client widening what this application may
 * reach inside their books would not, and the two belong behind the same
 * door rather than one looking like a setting and the other a request.
 *
 * Turning Projects on cannot grant anything by itself — a Xero scope is
 * granted by the client at Xero's consent screen. So it records the
 * decision and the screen then asks for a reconnect, which is the honest
 * order: the switch says what was agreed, the client's consent makes it
 * true.
 */

export function ClientFeatures({
  features,
  busy,
  error,
  reconnectHref,
  needsReconnect,
  t,
  onChange,
}: {
  features: { training: boolean; xeroProjects: boolean; amountsAre: LineAmountType };
  busy: boolean;
  error: string | null;
  /** Where to send them to consent, pinned to this company. */
  reconnectHref: string;
  /** The connection is short of a permission this company now expects. */
  needsReconnect: boolean;
  t: Translate;
  onChange: (change: {
    training?: boolean;
    xeroProjects?: boolean;
    amountsAre?: LineAmountType;
  }) => void;
}) {
  return (
    <Card style={{ marginBottom: '14px' }}>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.dividerSoft}`,
          fontWeight: 600,
          fontSize: '13px',
          background: theme.surfaceHeader,
        }}
      >
        {t('What this client has')}
      </div>

      <Switch
        label={t('Training')}
        hint={t('Courses, cohorts, student invoicing and what each course made. Nothing in Xero changes.')}
        on={features.training}
        busy={busy}
        t={t}
        onToggle={(on) => onChange({ training: on })}
      />

      <Switch
        label={t('Xero Projects')}
        hint={t('Asks this client for the Projects permission in Xero, which lets trainer hours be costed to a course. Training works without it.')}
        on={features.xeroProjects}
        busy={busy}
        t={t}
        /*
          Said before they press it, not after. Turning this on puts a
          reconnect in front of the client, and that is a thing to warn
          somebody about rather than spring on them.
        */
        confirm={
          features.xeroProjects
            ? null
            : t('This asks the client to connect Xero again, so they can approve the new permission. Turn it on?')
        }
        onToggle={(on) => onChange({ xeroProjects: on })}
      />

      {/*
        Not a switch, because there are three answers and two of them are
        not "off". This is where a new document starts for this client;
        whoever fills one in can still change it on that document.

        Qatar has no VAT regime, so "No tax" is right for most clients here
        and Xero's own "Tax exclusive" is wrong on every document until
        somebody remembers to change it — wrong silently, because the
        document still files. Set once, per client, instead.
      */}
      <div style={{ padding: '13px 16px', borderTop: `1px solid ${theme.dividerSoft}` }}>
        <label
          htmlFor="client-amounts-are"
          style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}
        >
          {t('New documents start as')}
        </label>
        <div
          style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.6, marginBottom: '8px' }}
        >
          {t(
            'How amounts are stated on a new invoice or bill for this client. They can still change it on the document itself.',
          )}
        </div>
        <select
          id="client-amounts-are"
          value={features.amountsAre}
          disabled={busy}
          onChange={(e) => onChange({ amountsAre: e.target.value as LineAmountType })}
          style={{
            height: '34px',
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            padding: '0 10px',
            fontSize: '13px',
            background: '#fff',
            minWidth: '220px',
          }}
        >
          <option value="Exclusive">{t('Tax exclusive')}</option>
          <option value="Inclusive">{t('Tax inclusive')}</option>
          <option value="NoTax">{t('No tax')}</option>
        </select>
      </div>

      {needsReconnect && (
        <div
          style={{
            padding: '13px 16px',
            borderTop: `1px solid ${theme.dividerSoft}`,
            background: '#F8EDEC',
          }}
        >
          <div style={{ fontSize: '12.5px', color: '#7E590F', lineHeight: 1.6 }}>
            {t('This connection has not been granted everything this client is now set up for. Reconnecting asks Xero for it.')}
          </div>
          <a
            href={reconnectHref}
            style={{
              display: 'inline-block',
              marginTop: '10px',
              background: theme.nav,
              color: '#fff',
              borderRadius: theme.radiusControl,
              padding: '8px 14px',
              fontSize: '12.5px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            {t('Reconnect this organisation')}
          </a>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '11px 16px',
            borderTop: `1px solid ${theme.dividerSoft}`,
            fontSize: '12.5px',
            color: theme.badDeep,
            fontWeight: 600,
          }}
        >
          {t(error)}
        </div>
      )}
    </Card>
  );
}

/** One switch, with the sentence that says what it does. */
function Switch({
  label,
  hint,
  on,
  busy,
  confirm,
  t,
  onToggle,
}: {
  label: string;
  hint: string;
  on: boolean;
  busy: boolean;
  /** Asked before turning on, where turning on has a consequence. */
  confirm?: string | null;
  t: Translate;
  onToggle: (on: boolean) => void;
}) {
  const [asking, setAsking] = useState(false);

  const press = () => {
    if (!on && confirm) {
      setAsking(true);
      return;
    }
    onToggle(!on);
  };

  return (
    <div style={{ padding: '14px 16px', borderTop: `1px solid ${theme.dividerSoft}` }}>
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={label}
          disabled={busy}
          onClick={press}
          style={{
            flex: '0 0 auto',
            width: '44px',
            height: '25px',
            marginTop: '1px',
            borderRadius: '13px',
            border: `1px solid ${on ? theme.nav : theme.borderStrong}`,
            background: on ? theme.nav : '#fff',
            cursor: busy ? 'default' : 'pointer',
            padding: 0,
            position: 'relative',
            // The knob sits on the side the language reads towards, so the
            // switch travels the way the page does.
            display: 'flex',
            justifyContent: on ? 'flex-end' : 'flex-start',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              display: 'block',
              width: '19px',
              height: '19px',
              margin: '0 2px',
              borderRadius: '50%',
              background: on ? '#fff' : theme.borderStrong,
            }}
          />
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>
            {label}{' '}
            <span style={{ fontWeight: 400, color: on ? theme.goodDeep : theme.textMuted }}>
              · {on ? t('on') : t('off')}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.6, marginTop: '3px' }}>
            {hint}
          </div>
        </div>
      </div>

      {asking && confirm && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px 14px',
            background: theme.surfaceHeader,
            border: `1px solid ${theme.border}`,
            borderRadius: theme.radiusControl,
          }}
        >
          <div style={{ fontSize: '12.5px', lineHeight: 1.6 }}>{confirm}</div>
          <div style={{ display: 'flex', gap: '9px', marginTop: '10px' }}>
            <button
              type="button"
              onClick={() => {
                setAsking(false);
                onToggle(true);
              }}
              style={{
                height: '32px',
                padding: '0 14px',
                background: theme.nav,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Turn it on')}
            </button>
            <button
              type="button"
              onClick={() => setAsking(false)}
              style={{
                height: '32px',
                padding: '0 14px',
                background: 'transparent',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
